import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { AssetCard } from "@/components/AssetCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Browse() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState(params.get("q") || "");

  const category = params.get("category") || "all";
  const type = params.get("type") || "all";
  const sort = params.get("sort") || "newest";

  const update = useCallback(
    (key, value) => {
      const next = new URLSearchParams(params);
      if (value && value !== "all") next.set(key, value);
      else next.delete(key);
      setParams(next);
    },
    [params, setParams]
  );

  useEffect(() => {
    api.get("/assets/categories").then((r) => setCategories(r.data.categories));
  }, []);

  const buildParams = useCallback(
    (pageNum) => {
      const p = { page: pageNum, limit: 60, sort };
      if (params.get("q")) p.q = params.get("q");
      if (category !== "all") p.category = category;
      if (type !== "all") p.asset_type = type;
      return p;
    },
    [params, category, type, sort]
  );

  useEffect(() => {
    setLoading(true);
    setPage(1);
    api.get("/assets", { params: buildParams(1) }).then((r) => {
      setAssets(r.data.assets);
      setTotal(r.data.total || r.data.assets.length);
      setLoading(false);
    });
  }, [params, category, type, sort, buildParams]);

  const loadMore = () => {
    const nextPage = page + 1;
    setLoadingMore(true);
    api.get("/assets", { params: buildParams(nextPage) }).then((r) => {
      setAssets((prev) => [...prev, ...r.data.assets]);
      setPage(nextPage);
      setLoadingMore(false);
    });
  };

  const submitSearch = (e) => {
    e.preventDefault();
    update("q", q);
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-10">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">
            {t("nav.browse")}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {total.toLocaleString("id-ID")} {t("common.results")}
          </p>
        </div>
        <form onSubmit={submitSearch} className="relative w-full max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("nav.searchPlaceholder")}
            className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-sm outline-none focus:border-blue-500"
            data-testid="browse-search-input"
          />
        </form>
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <SlidersHorizontal size={15} /> {t("common.filters")}
        </span>

        {/* Type pills */}
        <div className="flex gap-1.5">
          {[
            { v: "all", l: t("common.all") },
            { v: "photo", l: t("common.photo") },
            { v: "graphic", l: t("common.graphic") },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => update("type", opt.v)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
                type === opt.v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid={`filter-type-${opt.v}`}
            >
              {opt.l}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-3">
          <Select value={category} onValueChange={(v) => update("category", v)}>
            <SelectTrigger className="w-[170px]" data-testid="filter-category-select">
              <SelectValue placeholder={t("detail.category")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all")}</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={sort} onValueChange={(v) => update("sort", v)}>
            <SelectTrigger className="w-[180px]" data-testid="filter-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">{t("common.sortNewest")}</SelectItem>
              <SelectItem value="popular">{t("common.sortPopular")}</SelectItem>
              <SelectItem value="price_low">{t("common.sortPriceLow")}</SelectItem>
              <SelectItem value="price_high">{t("common.sortPriceHigh")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Active filters */}
      {(params.get("q") || category !== "all" || type !== "all") && (
        <div className="mt-4 flex flex-wrap gap-2">
          {params.get("q") && (
            <FilterChip label={`"${params.get("q")}"`} onClear={() => { setQ(""); update("q", ""); }} />
          )}
          {category !== "all" && <FilterChip label={category} onClear={() => update("category", "all")} />}
          {type !== "all" && (
            <FilterChip label={type === "photo" ? t("common.photo") : t("common.graphic")} onClear={() => update("type", "all")} />
          )}
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[4/3] animate-pulse rounded-2xl bg-slate-100" />
          ))}
        </div>
      ) : assets.length === 0 ? (
        <div className="mt-20 text-center">
          <p className="text-lg font-semibold text-slate-900">{t("common.noResults")}</p>
        </div>
      ) : (
        <>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {assets.map((a, i) => (
              <AssetCard key={a.id} asset={a} index={i} />
            ))}
          </div>
          {assets.length < total && (
            <div className="mt-10 flex justify-center">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="rounded-full bg-slate-900 px-8 py-3 text-sm font-semibold text-white transition hover:bg-slate-700 disabled:opacity-60"
                data-testid="load-more-btn"
              >
                {loadingMore ? t("common.loading") : `${t("common.loadMore")} (${(total - assets.length).toLocaleString("id-ID")})`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FilterChip({ label, onClear }) {
  return (
    <span className="flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
      {label}
      <button onClick={onClear} className="hover:text-blue-900">
        <X size={12} />
      </button>
    </span>
  );
}
